// Voice Quality Optimizer for mobile Safari and other browsers
// This utility helps improve TTS quality on problematic devices

export class VoiceQualityOptimizer {
  constructor() {
    this.isMobileSafari = this.detectMobileSafari();
    this.isMobile = this.detectMobile();
    this.voiceQuality = this.assessVoiceQuality();
    this.optimizationLevel = this.determineOptimizationLevel();
  }

  detectMobileSafari() {
    const userAgent = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  assessVoiceQuality() {
    // Assess the current device's voice quality capabilities
    if (this.isMobileSafari) {
      return 'low'; // Mobile Safari typically has poor voice quality
    } else if (this.isMobile) {
      return 'medium'; // Other mobile browsers vary
    } else {
      return 'high'; // Desktop browsers generally have good quality
    }
  }

  determineOptimizationLevel() {
    switch (this.voiceQuality) {
      case 'low':
        return 'aggressive';
      case 'medium':
        return 'moderate';
      case 'high':
        return 'minimal';
      default:
        return 'moderate';
    }
  }

  getOptimizedVoiceSettings() {
    const baseSettings = {
      lang: 'en-US',
      pitch: 1.0,
      volume: 1.0
    };

    switch (this.optimizationLevel) {
      case 'aggressive':
        return {
          ...baseSettings,
          rate: 0.7,        // Much slower for clarity
          volume: 0.9,      // Slightly lower volume
          pitch: 1.0        // Normal pitch
        };
      case 'moderate':
        return {
          ...baseSettings,
          rate: 0.8,        // Moderately slower
          volume: 0.95,     // Slightly lower volume
          pitch: 1.0        // Normal pitch
        };
      case 'minimal':
        return {
          ...baseSettings,
          rate: 0.75,       // Slightly slower
          volume: 1.0,      // Full volume
          pitch: 1.0        // Normal pitch
        };
      default:
        return baseSettings;
    }
  }

  getPreferredVoiceTypes() {
    if (this.isMobileSafari) {
      // iOS Safari works best with these voices
      return [
        'Samantha',  // High quality on iOS
        'Alex',      // High quality on iOS
        'Karen',     // Good quality on iOS
        'Daniel',    // Good quality on iOS
        'Victoria',  // Good quality on iOS
        'Fred'       // Good quality on iOS
      ];
    } else if (this.isMobile) {
      // Other mobile browsers
      return [
        'Google US English Female',
        'Google US English Male',
        'Samantha',
        'Alex'
      ];
    } else {
      // Desktop browsers
      return [
        'Google US English Female',
        'Google US English Male',
        'Samantha',
        'Alex',
        'Karen',
        'Daniel'
      ];
    }
  }

  findBestAvailableVoice(availableVoices, preferredLanguage = 'en-US') {
    if (!availableVoices || availableVoices.length === 0) {
      return null;
    }

    const preferredTypes = this.getPreferredVoiceTypes();
    
    // First, try to find a preferred voice type
    for (const voiceType of preferredTypes) {
      const found = availableVoices.find(v => 
        v.name.includes(voiceType) && 
        v.lang.startsWith(preferredLanguage)
      );
      if (found) return found;
    }

    // If no preferred voice found, look for any voice with the right language
    const languageMatch = availableVoices.find(v => 
      v.lang.startsWith(preferredLanguage)
    );
    if (languageMatch) return languageMatch;

    // Last resort: return the first available voice
    return availableVoices[0];
  }

  createOptimizedUtterance(text, voice = null) {
    if (!('speechSynthesis' in window)) {
      throw new Error('Speech synthesis not supported');
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const optimizedSettings = this.getOptimizedVoiceSettings();

    // Apply optimized settings
    Object.assign(utterance, optimizedSettings);

    // Set voice if provided
    if (voice) {
      utterance.voice = voice;
    }

    // Mobile Safari specific optimizations
    if (this.isMobileSafari) {
      // iOS Safari works better with clean text
      utterance.text = text.trim();
      
      // Add a small delay for iOS Safari
      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 100);
    } else {
      // For other browsers, speak immediately
      window.speechSynthesis.speak(utterance);
    }

    return utterance;
  }

  // Enhanced speak function with quality optimization
  speakWithOptimization(text, availableVoices, onStart, onEnd, onError) {
    try {
      // Stop any current speech
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }

      // Find the best available voice
      const bestVoice = this.findBestAvailableVoice(availableVoices);
      
      // Create and configure the utterance
      const utterance = this.createOptimizedUtterance(text, bestVoice);

      // Set up event handlers
      utterance.onstart = () => {
        if (onStart) onStart(text);
      };

      utterance.onend = () => {
        if (onEnd) onEnd();
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        if (onError) onError(event);
      };

      return utterance;
    } catch (error) {
      console.error('Error in speakWithOptimization:', error);
      if (onError) onError(error);
      return null;
    }
  }

  // Get optimization recommendations for the current device
  getOptimizationRecommendations() {
    const recommendations = [];

    if (this.isMobileSafari) {
      recommendations.push(
        'Using iOS Safari - voice quality may be limited',
        'Consider using headphones for better audio',
        'Voice rate has been optimized for clarity',
        'Using iOS-optimized voice selection'
      );
    } else if (this.isMobile) {
      recommendations.push(
        'Mobile device detected - voice settings optimized',
        'Voice rate adjusted for mobile clarity',
        'Using mobile-optimized voice selection'
      );
    } else {
      recommendations.push(
        'Desktop browser detected - optimal voice quality',
        'Using standard voice settings'
      );
    }

    return recommendations;
  }
}

// Export a singleton instance
export const voiceQualityOptimizer = new VoiceQualityOptimizer();

// Export convenience functions
export const getOptimizedVoiceSettings = () => voiceQualityOptimizer.getOptimizedVoiceSettings();
export const findBestAvailableVoice = (voices, language) => voiceQualityOptimizer.findBestAvailableVoice(voices, language);
export const speakWithOptimization = (text, voices, onStart, onEnd, onError) => 
  voiceQualityOptimizer.speakWithOptimization(text, voices, onStart, onEnd, onError);
